import { type MergeTagMenuEntry } from "./signature-merge-tags.utils";

/*
 * ENG-2492 device tags, one spec per kind of line that is fitted to an ear (ENG-2501 hearing aids, ENG-2504
 * earmolds, receivers and domes). Each kind has a Left, a Right and a No-side slot; a slot's fields are the
 * invoice table's columns for that kind plus the values Anvil fills. Left and Right of a kind that already had
 * per-ear tags (Patient.SelectedInvoice.LeftHearingAid.*, LeftReceiver.*, LeftDome.*) keep those tags and gain
 * the rest, so a form never holds two tags for one value.
 */

export type Field = { tag: string; label: string };
export type EarSlot = "LEFT" | "RIGHT" | "NO_SIDE";
export type EarKind = "HEARING_AID" | "EARMOLD" | "RECEIVER" | "DOME" | "TUBE";
export type NumberedKind = "BATTERY" | "CHARGER" | "REMOTE" | "OTHER";
export type DeviceKind = EarKind | NumberedKind;

const field = (tag: string, label = tag): Field => ({ tag, label });

const IDENTITY = [
  field("Manufacturer"),
  field("Model"),
  field("Name", "Name (as in your Catalog)"),
  field("SerialNumber", "Serial Number"),
  field("WarrantyEnd", "Manufacturer Warranty End Date")
];

const LINE_MONEY = [
  field("CPTCode", "CPT Code"),
  field("Quantity"),
  field("UnitPrice", "Unit Price"),
  field("ListPrice", "List Price"),
  field("Discount"),
  field("Price", "Price After Discount"),
  field("TotalWithTax", "Total With Tax"),
  field("PatientResponsibility", "Patient Responsibility"),
  field("InsuranceResponsibility", "Insurance Responsibility")
];

const LOSS_DAMAGE = [
  field("LossDamageWarrantyEnd", "L&D Warranty End Date"),
  field("LossDamageDeductible", "L&D Deductible")
];

const HEARING_AID_FIELDS = [
  ...IDENTITY,
  field("Product"),
  field("Ear"),
  field("Color"),
  field("Battery"),
  field("Condition"),
  ...LINE_MONEY,
  ...LOSS_DAMAGE,
  field("ServicePlanEnd", "Service Plan End Date")
];

const ACCESSORY_FIELDS = [
  ...IDENTITY,
  field("Product"),
  field("Ear"),
  field("Color"),
  field("Condition"),
  ...LINE_MONEY,
  ...LOSS_DAMAGE
];

// Anvil's values about a kind as a whole (`<kind>_make_model`, `<kind>_warranty_repair`, …).
const KIND_TOTALS = [
  field("MakeAndModel", "Make and Model"),
  field("Manufacturer"),
  field("Model"),
  field("RepairWarranty", "Repair Warranty"),
  field("LossDamageWarranty", "L&D Warranty"),
  field("LossDamageDeductible", "L&D Deductible"),
  field("ListTotal", "List Total"),
  field("TotalWithTax", "Total With Tax"),
  field("TotalPlusServices", "Total Plus Services"),
  field("WarrantyYears", "Warranty Years")
];

type KindSpec = {
  /** The slot tags are `Left<stem>`, `Right<stem>` and `NoSide<stem>`. */
  stem: string;
  label: string;
  group: string;
  fields: Field[];
  /** Fields Left and Right already had before ENG-2492. */
  existingLeftRight: string[];
  both?: { tag: string; label: string; fields: Field[] };
};

export const PA_EAR_KINDS: Record<EarKind, KindSpec> = {
  HEARING_AID: {
    stem: "HearingAid",
    label: "Hearing Aid",
    group: "Hearing Aids",
    fields: HEARING_AID_FIELDS,
    existingLeftRight: IDENTITY.map(({ tag }) => tag),
    both: {
      tag: "HearingAids",
      label: "Both Hearing Aids",
      fields: [
        ...KIND_TOTALS.slice(0, 3),
        field("Options"),
        ...KIND_TOTALS.slice(3)
      ]
    }
  },
  EARMOLD: {
    stem: "Earmold",
    label: "Earmold",
    group: "Earmolds",
    fields: [
      ...ACCESSORY_FIELDS,
      field("Material"),
      field("CanalLength", "Canal Length"),
      field("CanalVenting", "Canal Venting")
    ],
    existingLeftRight: [],
    both: { tag: "Earmolds", label: "Both Earmolds", fields: KIND_TOTALS }
  },
  RECEIVER: {
    stem: "Receiver",
    label: "Receiver",
    group: "Receivers",
    fields: [...ACCESSORY_FIELDS, field("Gain"), field("Length")],
    existingLeftRight: ["Manufacturer", "Model", "Gain", "Length"]
  },
  DOME: {
    stem: "Dome",
    label: "Dome",
    group: "Domes",
    fields: [...ACCESSORY_FIELDS, field("Type"), field("Size")],
    existingLeftRight: ["Manufacturer", "Model", "Size", "Type"]
  },
  TUBE: {
    stem: "Tube",
    label: "Tube",
    group: "Tubes",
    fields: ACCESSORY_FIELDS,
    existingLeftRight: []
  }
};

/*
 * ENG-2505: kinds sold without an ear, numbered in the invoice table's order (Battery 1 is the first battery the
 * table prints). Anvil fills chargers, remotes and other accessories as a whole too (`charger_make_model`, …).
 */
type NumberedKindSpec = {
  /** The slot tags are `<stem>1`, `<stem>2`, … */
  stem: string;
  label: string;
  group: string;
  count: number;
  fields: Field[];
  total?: { tag: string; label: string; fields: Field[] };
};

export const PA_NUMBERED_KINDS: Record<NumberedKind, NumberedKindSpec> = {
  BATTERY: {
    stem: "Battery",
    label: "Battery",
    group: "Batteries",
    count: 2,
    fields: ACCESSORY_FIELDS
  },
  CHARGER: {
    stem: "Charger",
    label: "Charger",
    group: "Chargers",
    count: 2,
    fields: ACCESSORY_FIELDS,
    total: { tag: "Chargers", label: "All Chargers", fields: KIND_TOTALS }
  },
  REMOTE: {
    stem: "Remote",
    label: "Remote",
    group: "Remotes",
    count: 2,
    fields: ACCESSORY_FIELDS,
    total: { tag: "Remotes", label: "All Remotes", fields: KIND_TOTALS }
  },
  OTHER: {
    stem: "OtherAccessory",
    label: "Other Accessory",
    group: "Other Accessories",
    count: 3,
    fields: ACCESSORY_FIELDS,
    total: {
      tag: "OtherAccessories",
      label: "All Other Accessories",
      fields: KIND_TOTALS
    }
  }
};

const kindTotalSpec = (kind: DeviceKind) =>
  kind in PA_EAR_KINDS
    ? PA_EAR_KINDS[kind as EarKind].both
    : PA_NUMBERED_KINDS[kind as NumberedKind].total;

const SLOT_PREFIX: Record<EarSlot, string> = {
  LEFT: "Left",
  RIGHT: "Right",
  NO_SIDE: "NoSide"
};

const slotLabel = (kind: EarKind, slot: EarSlot) => {
  const { label } = PA_EAR_KINDS[kind];
  if (slot === "NO_SIDE") return `${label} Without An Ear`;
  return `${SLOT_PREFIX[slot]} ${label}`;
};

export const paEarTag = (kind: EarKind, slot: EarSlot, fieldTag: string) =>
  `Patient.SelectedInvoice.${SLOT_PREFIX[slot]}${PA_EAR_KINDS[kind].stem}.${fieldTag}`;

export const paKindTotalTag = (kind: DeviceKind, fieldTag: string) =>
  `Patient.SelectedInvoice.${kindTotalSpec(kind)?.tag}.${fieldTag}`;

export const paNumberedTag = (
  kind: NumberedKind,
  position: number,
  fieldTag: string
) =>
  `Patient.SelectedInvoice.${PA_NUMBERED_KINDS[kind].stem}${position}.${fieldTag}`;

export const numberedFieldTags = (kind: NumberedKind) =>
  PA_NUMBERED_KINDS[kind].fields.map(({ tag }) => tag);

/** The fields a slot gains: every field for No side, the ones Left and Right didn't have for them. */
const slotFields = (kind: EarKind, slot: EarSlot) => {
  const { fields, existingLeftRight } = PA_EAR_KINDS[kind];
  const existing = new Set(existingLeftRight);
  return fields.filter(({ tag }) => slot === "NO_SIDE" || !existing.has(tag));
};

export const earSlotFieldTags = (kind: EarKind, slot: EarSlot) =>
  slotFields(kind, slot).map(({ tag }) => tag);

export const kindTotalFieldTags = (kind: DeviceKind) =>
  kindTotalSpec(kind)?.fields.map(({ tag }) => tag) ?? [];

const slotEntries = (kind: EarKind, slot: EarSlot): MergeTagMenuEntry[] =>
  slotFields(kind, slot).map(({ tag, label }) => ({
    title: `Patient - Invoice - ${slotLabel(kind, slot)} - ${label}`,
    value: paEarTag(kind, slot, tag)
  }));

const kindTotalEntries = (kind: DeviceKind): MergeTagMenuEntry[] => {
  const total = kindTotalSpec(kind);
  if (!total) return [];
  return total.fields.map(({ tag, label }) => ({
    title: `Patient - Invoice - ${total.label} - ${label}`,
    value: paKindTotalTag(kind, tag)
  }));
};

const numberedEntries = (
  kind: NumberedKind,
  position: number
): MergeTagMenuEntry[] =>
  PA_NUMBERED_KINDS[kind].fields.map(({ tag, label }) => ({
    title: `Patient - Invoice - ${PA_NUMBERED_KINDS[kind].label} ${position} - ${label}`,
    value: paNumberedTag(kind, position, tag)
  }));

const positions = (kind: NumberedKind) =>
  Array.from({ length: PA_NUMBERED_KINDS[kind].count }, (_, i) => i + 1);

const EAR_KINDS = Object.keys(PA_EAR_KINDS) as EarKind[];
const NUMBERED_KINDS = Object.keys(PA_NUMBERED_KINDS) as NumberedKind[];
const EAR_SLOTS: EarSlot[] = ["LEFT", "RIGHT", "NO_SIDE"];

const values = (entries: MergeTagMenuEntry[]) =>
  entries.map(({ value }) => value as string);

export const DEVICE_TAG_VALUES: string[] = [
  ...EAR_KINDS.flatMap((kind) => [
    ...EAR_SLOTS.flatMap((slot) => values(slotEntries(kind, slot))),
    ...values(kindTotalEntries(kind))
  ]),
  ...NUMBERED_KINDS.flatMap((kind) => [
    ...positions(kind).flatMap((position) =>
      values(numberedEntries(kind, position))
    ),
    ...values(kindTotalEntries(kind))
  ])
];

/**
 * One group per kind ("Hearing Aids", "Earmolds", …) holding Left, Right, Without An Ear and, where Anvil has
 * kind-level values, Both. An existing per-ear group of the invoice menu ("Left Receiver") keeps its entries and
 * gains the new ones; the kind groups stand where the first existing device group stood. The existing loose
 * "Both Hearing Aids - …" entries stay where they are.
 */
export const withDeviceGroups = (
  menu: MergeTagMenuEntry[]
): MergeTagMenuEntry[] => {
  const existing = new Map(
    menu.filter((entry) => entry.menu).map((entry) => [entry.title, entry])
  );

  const kindGroups: MergeTagMenuEntry[] = EAR_KINDS.map((kind) => {
    const slots = EAR_SLOTS.map((slot) => {
      const title = slotLabel(kind, slot);
      const current = existing.get(title);
      return {
        ...(current ?? { title }),
        menu: [...(current?.menu ?? []), ...slotEntries(kind, slot)]
      };
    });
    const both = PA_EAR_KINDS[kind].both;
    const bothGroup = both
      ? [{ title: both.label, menu: kindTotalEntries(kind) }]
      : [];
    return { title: PA_EAR_KINDS[kind].group, menu: [...slots, ...bothGroup] };
  });

  const numberedGroups: MergeTagMenuEntry[] = NUMBERED_KINDS.map((kind) => {
    const { label, group, total } = PA_NUMBERED_KINDS[kind];
    return {
      title: group,
      menu: [
        ...positions(kind).map((position) => ({
          title: `${label} ${position}`,
          menu: numberedEntries(kind, position)
        })),
        ...(total ? [{ title: total.label, menu: kindTotalEntries(kind) }] : [])
      ]
    };
  });

  const replaced = new Set(
    EAR_KINDS.flatMap((kind) =>
      (["LEFT", "RIGHT"] as EarSlot[]).map((slot) => slotLabel(kind, slot))
    )
  );
  const firstIndex = menu.findIndex((entry) => replaced.has(entry.title));
  const rest = menu.filter((entry) => !replaced.has(entry.title));
  const at = firstIndex < 0 ? rest.length : firstIndex;
  return [
    ...rest.slice(0, at),
    ...kindGroups,
    ...numberedGroups,
    ...rest.slice(at)
  ];
};
